// ============================================================================
// Deduct Credit dialog — single-brand version, for use from a table row action
// ============================================================================
// The removal counterpart of GrantCreditDialog.tsx (which is untouched by this
// file — no shared state, no shared mutation). Styled deliberately differently
// (red/destructive, not violet) so the two actions are never visually
// confusable on a table row that offers both.
//
// Nothing about the authorization is re-implemented here: every check
// (permission, per-deduction/rolling-24h caps, available balance,
// self-dealing) is read from admin_my_credit_deduct_context() — the same
// function admin_deduct_brand_credit() enforces against — via
// fetchCreditDeductContext(). This component only decides whether to let the
// "Deduct" button be clickable; the database decides whether the deduction
// actually happens.
// ============================================================================

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingDown, Loader2, ShieldAlert, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { usd } from '@/lib/refunds';
import { logCreate } from '@/lib/audit-log';
import {
  fetchCreditDeductContext, deductBrandCredit, parseDollarsToCents, TRANSACTION_PLATFORM_OPTIONS,
} from '@/lib/adminCredits';

export interface DeductCreditTarget {
  id: string;
  name?: string | null;
}

export function DeductCreditDialog({ brand, onOpenChange, onDeducted }: {
  /** null = closed. Passing a new brand while open resets the form. */
  brand: DeductCreditTarget | null;
  onOpenChange: (open: boolean) => void;
  onDeducted?: () => void;
}) {
  const open = !!brand;
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // OPTIONAL, unlike the grant dialog — a deduction isn't always tied to one
  // external payment. '' (unselected) maps to `undefined` on submit.
  const [platform, setPlatform] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // One key per opened dialog: retries of THIS deduction collapse into a
  // no-op — a double-click or a stale tab replays instead of deducting twice.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!brand) return;
    setAmount('');
    setReason('');
    setPlatform('');
    setTransactionId('');
    setNote('');
    setError(null);
    setIdemKey(crypto.randomUUID());
  }, [brand?.id]);

  const { data: context, isLoading, isError, error: contextError } = useQuery({
    queryKey: ['credit-deduct-context', brand?.id],
    queryFn: () => fetchCreditDeductContext(brand?.id ?? null),
    enabled: open,
  });

  const parsed = parseDollarsToCents(amount);
  const amountCents = parsed.cents;
  const limits = context?.limits;
  const liveBrand = context?.brand;
  const balanceCents = liveBrand?.balance_cents ?? 0;
  const availableCents = liveBrand?.available_cents ?? 0;
  const selfDealing = liveBrand?.is_member_of_brand === true;

  const blocker = !context
    ? null
    : !context.can_deduct
      ? (context.blocked_reason ?? 'You are not permitted to deduct brand credit.')
      : parsed.error
        ? parsed.error
        : amountCents <= 0
          ? 'Enter an amount'
          : limits && amountCents < limits.min_deduct_cents
            ? `Minimum deduction is ${usd(limits.min_deduct_cents)}`
            : amountCents > availableCents
              ? `Only ${usd(availableCents)} is spendable on this brand right now`
              : limits && amountCents > limits.remaining_24h_cents
                ? `Over your remaining 24h allowance of ${usd(limits.remaining_24h_cents)}`
                : reason.trim().length < 3
                  ? 'Add a reason — the brand sees it on their wallet history'
                  : reason.trim().length > 500
                    ? 'Reason must be 500 characters or fewer'
                    : null;

  async function submit() {
    if (!brand || blocker) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await deductBrandCredit({
        brandId: brand.id,
        amountCents,
        reason: reason.trim(),
        transactionPlatform: platform || undefined,
        transactionId: transactionId.trim() || undefined,
        internalNote: note.trim() || undefined,
        idempotencyKey: idemKey,
      });

      logCreate('brand_credit_deductions', result.deduction_id, {
        brand_id: result.brand_id,
        amount_cents: result.deducted_cents,
        reason: reason.trim(),
        transaction_platform: result.transaction_platform,
        transaction_id: result.transaction_id,
        internal_note: note.trim() || null,
        ledger_entry_id: result.ledger_id,
        balance_after_cents: result.balance_cents,
      }, {
        status: 'success',
        brand_name: result.brand_name,
        replayed: result.replayed,
        description: `Deducted ${usd(result.deducted_cents)} wallet credit from ${result.brand_name ?? result.brand_id}${result.replayed ? ' (replay — no new deduction)' : ''}`,
      });

      toast.success(
        result.replayed
          ? 'That deduction had already been applied — no new debit was created.'
          : `${usd(result.deducted_cents)} deducted from ${result.brand_name ?? 'the brand'}`,
        { description: `New balance ${usd(result.balance_cents)}` },
      );

      queryClient.invalidateQueries({ queryKey: ['brand-users'] });
      queryClient.invalidateQueries({ queryKey: ['credit-deduct-context'] });
      queryClient.invalidateQueries({ queryKey: ['credit-grant-context'] });
      queryClient.invalidateQueries({ queryKey: ['credit-deduct-log'] });
      queryClient.invalidateQueries({ queryKey: ['credit-deduct-stats'] });
      onDeducted?.();
      onOpenChange(false);
    } catch (e: any) {
      const message = e?.message ?? 'Credit deduction failed';
      logCreate('brand_credit_deductions', brand.id, {
        brand_id: brand.id, amount_cents: amountCents, reason: reason.trim(),
      }, {
        status: 'failed',
        error: message,
        brand_name: brand.name,
        description: `Credit deduction of ${usd(amountCents)} from ${brand.name ?? brand.id} was rejected`,
      });
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-600" />
            Deduct credit — {brand?.name ?? 'this brand'}
          </DialogTitle>
          <DialogDescription>
            Removes spendable wallet credit. This is the brand's money coming out of their account —
            treat it with the same care as a refund, in reverse.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <Skeleton className="h-48 w-full rounded-lg" />}

        {isError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(contextError as any)?.message ?? 'Could not confirm your deduction permissions.'}</span>
          </div>
        )}

        {context && !context.can_deduct && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{context.blocked_reason ?? 'Your account cannot deduct brand credit.'}</span>
          </div>
        )}

        {context?.can_deduct && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-muted-foreground">Current balance</span>
                <span className="font-semibold tabular-nums">{usd(balanceCents)}</span>
              </div>
              {liveBrand && liveBrand.held_cents > 0 && (
                <div className="mt-0.5 flex items-baseline justify-between text-xs text-muted-foreground">
                  <span>Reserved (not deductible)</span><span className="tabular-nums">{usd(liveBrand.held_cents)}</span>
                </div>
              )}
              <div className="mt-0.5 flex items-baseline justify-between text-xs text-muted-foreground">
                <span>Spendable — the max you can deduct</span>
                <span className="font-medium tabular-nums text-foreground">{usd(availableCents)}</span>
              </div>
              {amountCents > 0 && (
                <div className="mt-1.5 flex items-baseline justify-between border-t pt-1.5">
                  <span className="text-xs text-muted-foreground">After this deduction</span>
                  <span className="text-sm font-semibold tabular-nums text-red-700">
                    {usd(Math.max(0, balanceCents - amountCents))}
                  </span>
                </div>
              )}
            </div>

            {selfDealing && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <Ban className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  You're a member of this brand's team, so you can't deduct from it — the database refuses this
                  combination regardless of your permission. Another admin has to issue it.
                </span>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="dcd-amount" className="text-sm font-medium">Amount</label>
              <div className="relative w-36">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  id="dcd-amount"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(null); }}
                  placeholder="50.00"
                  inputMode="decimal"
                  className="pl-6 font-semibold tabular-nums"
                  autoFocus
                />
              </div>
              {parsed.error && <p className="text-xs text-red-500">{parsed.error}</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="dcd-reason" className="text-sm font-medium">
                Reason <span className="font-normal text-muted-foreground">— shown to the brand</span>
              </label>
              <Input
                id="dcd-reason"
                value={reason}
                onChange={(e) => { setReason(e.target.value.slice(0, 500)); setError(null); }}
                placeholder="Chargeback correction on a prior goodwill credit"
                maxLength={500}
              />
              <p className={`text-[11px] ${reason.trim().length > 500 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {reason.trim().length > 500 ? 'Max 500 characters allowed' : `${reason.length}/500 characters`}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="dcd-platform" className="text-sm font-medium">
                  Payment method <span className="font-normal text-muted-foreground">— optional</span>
                </label>
                <Select value={platform} onValueChange={(v) => { setPlatform(v); setError(null); }}>
                  <SelectTrigger id="dcd-platform"><SelectValue placeholder="Not tied to one payment" /></SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_PLATFORM_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="dcd-txn" className="text-sm font-medium">
                  Transaction ID <span className="font-normal text-muted-foreground">— optional</span>
                </label>
                <Input
                  id="dcd-txn"
                  value={transactionId}
                  onChange={(e) => { setTransactionId(e.target.value); setError(null); }}
                  placeholder="e.g. the Whop refund this reconciles"
                  maxLength={200}
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Fill these in when this deduction reconciles a specific external refund. Leave blank for
              a plain balance correction.
            </p>

            <div className="space-y-1.5">
              <label htmlFor="dcd-note" className="text-sm font-medium">
                Internal note <span className="font-normal text-muted-foreground">— never shown to the brand (optional)</span>
              </label>
              <Textarea
                id="dcd-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Approved by… / ticket reference"
              />
            </div>

            {limits && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Your rolling 24h deduction allowance</span>
                  <span className="font-medium text-foreground">
                    {usd(limits.remaining_24h_cents)} left of {usd(limits.daily_limit_cents)}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-red-500 transition-all"
                    style={{
                      width: `${limits.daily_limit_cents > 0
                        ? Math.min(100, Math.round((limits.deducted_24h_cents / limits.daily_limit_cents) * 100))
                        : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <p className="font-semibold">Deduction refused</p>
                <p className="mt-0.5">{error}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!context?.can_deduct || !!blocker || submitting}
            onClick={submit}
          >
            {submitting
              ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Deducting…</>
              : amountCents > 0 ? `Deduct ${usd(amountCents)}` : 'Deduct credit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
